import { FormProvider, useForm, type DefaultValues, type SubmitHandler, type UseFormProps, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ReactNode } from 'react';
import type { ZodTypeAny, z } from 'zod';

interface FormProps<TSchema extends ZodTypeAny> {
  schema: TSchema;
  defaultValues?: DefaultValues<z.infer<TSchema>>;
  onSubmit: SubmitHandler<z.infer<TSchema>>;
  children: ReactNode | ((methods: UseFormReturn<z.infer<TSchema>>) => ReactNode);
  options?: Omit<UseFormProps<z.infer<TSchema>>, 'resolver' | 'defaultValues'>;
  className?: string;
  id?: string;
}

export function Form<TSchema extends ZodTypeAny>({
  schema,
  defaultValues,
  onSubmit,
  children,
  options,
  className,
  id,
}: FormProps<TSchema>) {
  type Values = z.infer<TSchema>;
  const methods = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onBlur',
    ...options,
  });

  return (
    <FormProvider {...methods}>
      <form
        id={id}
        className={className}
        onSubmit={(event) => {
          void methods.handleSubmit(onSubmit)(event);
        }}
        noValidate
      >
        {typeof children === 'function' ? children(methods) : children}
      </form>
    </FormProvider>
  );
}
